import React, { Fragment, useEffect, useState } from 'react';
import { ButtonGroupRadioSwitch } from 'components/ButtonGroupRadioSwitch';
import { Card } from 'components/Card';
import { InfoTooltip } from 'components/InfoTooltip';
import { SwitchControl } from 'components/SwitchControl';
import { Box, Stack, Typography } from '@mui/material';
import { CampaignFields } from 'types';
import { useCampaignForm, useIsMobile } from 'hooks';
import { TargetingConfiguration } from './TargetingConfiguration';
import { TargetingTextDescription } from './TargetingTextDescription';
import { useCampaignTargetingGeneration } from 'hooks/useCampaignTargetingGeneration';

export const TargetingGuidance = () => {
  const { watch, setValue } = useCampaignForm();
  const { isMobile } = useIsMobile();
  const targetingGuidanceFormat = watch(CampaignFields.targetingGuidanceFormat);
  const targetingGuidanceMode = watch(CampaignFields.targetingGuidanceMode);
  const websites = watch(CampaignFields.websites);
  const [isAllSuggestionsReady, setIsAllSuggestionsReady] = useState(false);
  const { getCampaignTargetingSuggestions, generateCampaignTargetingSuggestions } =
    useCampaignTargetingGeneration(!isAllSuggestionsReady);

  const [firstWebsiteSuggestions] = getCampaignTargetingSuggestions?.data?.suggestions || [];

  const isSuggestionsReady = getCampaignTargetingSuggestions?.data?.suggestions?.every((item) => item.ready);

  const onTargetingGuidanceFormatChange = (value: string) => {
    setValue(CampaignFields.targetingGuidanceFormat, value);
  };

  const onTargetingsModeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked;

    setValue(CampaignFields.targetingGuidanceMode, checked ? 'global' : 'perWebsite');
  };

  useEffect(() => {
    if (isSuggestionsReady) {
      setIsAllSuggestionsReady(true);
    } else {
      // enable optional polling when one or more suggestion entities are marked as ready: false
      setIsAllSuggestionsReady(false);
    }
  }, [isSuggestionsReady]);

  useEffect(() => {
    if (targetingGuidanceFormat !== 'json') {
      getCampaignTargetingSuggestions.refetch();
    }
    // trigger only on component mount to sync with api, as this data may be updated from another tab
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetingGuidanceFormat]);

  return (
    <>
      <Card>
        <Stack spacing={4}>
          <Box display="inline-block">
            <Typography display="inline" variant="subtitle1" sx={{ mr: 2 }}>
              Wähle eine Methode zur Zielgruppenerstellung (nur die gewählte wird verwendet)
            </Typography>
            <Box display="inline-block" sx={{ transform: 'translateY(-4px)' }}>
              <InfoTooltip
                placement="bottom"
                text={
                  <Typography variant="body2">
                    Wähle deine Zielgruppe klassisch per Klick aus – oder beschreibe sie frei in deinen eigenen Worten,
                    ganz wie bei einem Prompt.
                  </Typography>
                }
              />
            </Box>
          </Box>
          <ButtonGroupRadioSwitch
            preventDeselection
            multiple={false}
            clapToVertical={false}
            value={targetingGuidanceFormat}
            onChange={onTargetingGuidanceFormatChange}
            options={[
              { value: 'json', label: 'Konfigurieren' },
              { value: 'text', label: isMobile ? 'Beschreiben' : 'Mit Text beschreiben' },
            ]}
          />
          {websites.length > 1 ? (
            <SwitchControl
              htmlId="targeting-enable-global-config-switch"
              checked={targetingGuidanceMode === 'global'}
              onChange={onTargetingsModeChange}
              label={{
                text: 'Gleiche Zielgruppe für alle Webseiten',
                variant: 'body1',
              }}
              tooltip={{
                placement: 'bottom',
                text: (
                  <Stack spacing={2}>
                    <Typography variant="body2">
                      Du kannst eine gemeinsame Zielgruppe für alle Kampagnen-Webseiten festlegen – oder für jede
                      Webseite eine eigene Zielgruppe definieren.
                    </Typography>
                  </Stack>
                ),
              }}
            />
          ) : null}
        </Stack>
      </Card>
      {targetingGuidanceMode === 'global' ? (
        <>
          {targetingGuidanceFormat === 'json' && <TargetingConfiguration />}
          {targetingGuidanceFormat === 'text' && (
            <TargetingTextDescription
              suggestions={firstWebsiteSuggestions?.suggestions ?? []}
              isSuggestionLoading={!firstWebsiteSuggestions?.ready || false}
              handleGenerateSuggestion={() => {
                // when global targeting is enabled, generate suggestions only for the first website
                if (firstWebsiteSuggestions?.websiteUrl) {
                  generateCampaignTargetingSuggestions.mutate(firstWebsiteSuggestions.websiteUrl);
                }
              }}
            />
          )}
        </>
      ) : (
        <>
          {websites.map((website, index) => {
            const websiteSuggestionData = getCampaignTargetingSuggestions?.data?.suggestions?.find(
              (item) => item.websiteUrl === website.url
            );

            return (
              <Fragment key={website.dbId}>
                {targetingGuidanceFormat === 'json' && (
                  <TargetingConfiguration website={website} websiteIndex={index} />
                )}
                {targetingGuidanceFormat === 'text' && (
                  <TargetingTextDescription
                    website={website}
                    websiteIndex={index}
                    suggestions={websiteSuggestionData?.suggestions ?? []}
                    isSuggestionLoading={!websiteSuggestionData?.ready || false}
                    handleGenerateSuggestion={() => {
                      generateCampaignTargetingSuggestions.mutate(website.url);
                    }}
                  />
                )}
              </Fragment>
            );
          })}
        </>
      )}
    </>
  );
};
